import { Command } from 'commander';
import { confirm } from '@inquirer/prompts';
import { VaultCryptoService } from '../../services/vault-crypto.service.js';
import { VaultStoreService } from '../../services/vault-store.service.js';
import { VaultTrustedDeviceService } from '../../services/vault-trusted-device.service.js';
import { resolvePassphrase, type VaultUnlockOptions } from './shared.js';

interface TrustOptions extends VaultUnlockOptions {
    yes?: boolean;
    json?: boolean;
}

async function unlockForTrust(
    crypto: VaultCryptoService,
    store: VaultStoreService,
    options: TrustOptions
): Promise<Buffer> {
    const keyring = await store.loadKeyring();
    const recoveryKey = options.recoveryKey || process.env.JAI1_VAULT_RECOVERY_KEY;
    if (recoveryKey) return (await crypto.unlockWithRecoveryKey(keyring, recoveryKey)).vmk;

    const passphrase = await resolvePassphrase(options);
    if (!passphrase) {
        throw new Error('Cần passphrase. Dùng --passphrase hoặc JAI1_VAULT_PASSPHRASE.');
    }
    return (await crypto.unlockWithPassphrase(keyring, passphrase)).vmk;
}

export function createVaultTrustCommand(): Command {
    return new Command('trust')
        .description('Trust this local device for passphrase-free vault unlock')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('-y, --yes', 'Confirm trusting this device')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: TrustOptions) {
            const store = new VaultStoreService();
            if (!(await store.exists())) throw new Error('Vault chưa được khởi tạo. Chạy `j vault init` trước.');

            const keyring = await store.loadKeyring();
            const device = await store.loadDevice();
            if (!device) throw new Error('Không tìm thấy local device metadata. Hãy sync/pull hoặc init lại vault.');

            const trusted = new VaultTrustedDeviceService(store.getRootDir());
            if (!trusted.isSupported()) {
                throw new Error('Trust device hiện chỉ hỗ trợ macOS Keychain. Các OS khác hãy dùng JAI1_VAULT_PASSPHRASE hoặc --passphrase.');
            }

            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (!options.yes && !useJson) {
                console.log('Trust device sẽ lưu một local unlock key trong OS Keychain.');
                console.log('Passphrase không được lưu. Nếu máy/OS user bị truy cập, vault có thể unlock trên device này.');
                const accepted = await confirm({
                    message: 'Tin tưởng device này để không cần nhập passphrase cho các lần sau?',
                    default: false,
                });
                if (!accepted) {
                    console.log('Đã hủy trust device.');
                    return;
                }
            }

            const vmk = await unlockForTrust(new VaultCryptoService(), store, options);
            const record = await trusted.trust(keyring.vaultId, device, vmk);

            if (useJson) {
                console.log(JSON.stringify({
                    trusted: true,
                    vaultId: record.vaultId,
                    deviceId: record.deviceId,
                    deviceName: record.deviceName,
                }, null, 2));
                return;
            }

            console.log('Đã trust device này.');
            console.log(`Device: ${record.deviceName} (${record.deviceId})`);
            console.log('Từ giờ các lệnh vault sẽ thử unlock bằng OS Keychain trước khi hỏi passphrase.');
        });
}

export function createVaultUntrustCommand(): Command {
    return new Command('untrust')
        .description('Remove trusted-device vault unlock from this machine')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: { json?: boolean }) {
            const store = new VaultStoreService();
            if (!(await store.exists())) throw new Error('Vault chưa được khởi tạo. Chạy `j vault init` trước.');
            const keyring = await store.loadKeyring();
            const device = await store.loadDevice();
            await new VaultTrustedDeviceService(store.getRootDir()).untrust(keyring, device);

            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (useJson) {
                console.log(JSON.stringify({ trusted: false }, null, 2));
                return;
            }
            console.log('Đã gỡ trust device trên máy này.');
        });
}

export function createVaultTrustStatusCommand(): Command {
    return new Command('trust-status')
        .description('Show trusted-device unlock status')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, options: { json?: boolean }) {
            const store = new VaultStoreService();
            if (!(await store.exists())) throw new Error('Vault chưa được khởi tạo. Chạy `j vault init` trước.');
            const keyring = await store.loadKeyring();
            const device = await store.loadDevice();
            const status = await new VaultTrustedDeviceService(store.getRootDir()).status(keyring, device);

            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (useJson) {
                console.log(JSON.stringify(status, null, 2));
                return;
            }
            console.log(`Supported: ${status.supported ? 'yes' : 'no'}`);
            console.log(`Trusted: ${status.trusted ? 'yes' : 'no'}`);
            if (status.deviceName) console.log(`Device: ${status.deviceName} (${status.deviceId})`);
        });
}
